//-- Copyright (c) 2021 TMT Insights Group, LLC
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LoadingStatuses } from '../../constants/constants';
import { BulkSearchState, BulkUploadedFile } from './slice_types';
import { clearOrderPlacement, resumeDraft } from './orderPlacementSlice';
import axios from '@/_utils/axios';
import { AxiosResponse } from 'axios';

const initialState: BulkSearchState = {
  status: LoadingStatuses.fulfilled,
  draftOrderId: null,
  file: {} as BulkUploadedFile,
  autoSubmit: false,
  modalOrderId: null,
  titles: [],
  errors: [],
};

type BulkSearchTitlesPayload = {
  ids: string[];
  id_type: string
}

type BulkSearchTitlesResponse = {
  titles: BulkSearchState['titles'];
  errors: BulkSearchState['errors'];
}

export const bulkSearchTitles = createAsyncThunk<
  BulkSearchTitlesResponse,
  BulkSearchTitlesPayload
>('bulkSearch/searchTitles', async ({ ids, id_type }) => {
  return axios
    .post("/inventory/titles/batch-get/", { ids, id_type })
    .then((res: AxiosResponse<{ data: BulkSearchTitlesResponse }>) => res?.data?.data);
});

const bulkSearchSlice = createSlice({
  name: 'bulkSearch',
  initialState,
  reducers: {
    resetBulkSearch: () => initialState,
    clearBulkErrors: (state) => {
      state.errors = [];
    },
    removeBulkError: (state, { payload }) => {
      state.errors = state.errors.filter(error => error.id !== payload);
    },
    setBulkUploadedFile: (state, { payload }) => {
      state.file = payload;
    },
    toggleAutoSubmit: (state) => {
      state.autoSubmit = !state.autoSubmit;
    },
    setModalOrderId: (state, { payload }) => {
      state.modalOrderId = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(bulkSearchTitles.pending, (state: BulkSearchState) => {
      state.status = LoadingStatuses.pending;
      state.titles = [];
    });
    builder.addCase(bulkSearchTitles.fulfilled, (state: BulkSearchState, { payload }: PayloadAction<BulkSearchTitlesResponse>) => {
      const titles = (payload?.titles ?? []);
      const errors = (payload?.errors ?? []);
      state.titles = titles.filter((title) => title.entity_type === 'version');
      const nonVersionAsErrors = titles.filter((title) => title.entity_type !== 'version').map((title) => ({
        id: title.id?.toString(),
        error: 'Title is not a version'
      }));
      state.errors = [...state.errors, ...errors, ...nonVersionAsErrors];
      state.status = LoadingStatuses.fulfilled;
    });
    builder.addCase(bulkSearchTitles.rejected, (state: BulkSearchState) => {
      state.status = LoadingStatuses.fulfilled;
    });
    builder.addCase(resumeDraft, (state, { payload }) => {
      state.draftOrderId = payload.draftId;
      state.autoSubmit = payload.autoSubmit;
    });
    builder.addCase(clearOrderPlacement, () => initialState);
  }
});

export const {
  resetBulkSearch,
  removeBulkError,
  clearBulkErrors,
  setBulkUploadedFile,
  toggleAutoSubmit,
  setModalOrderId
} = bulkSearchSlice.actions;

export default bulkSearchSlice.reducer;
